import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses';
import { upsertContactUser } from '../../common/fetch';
import {
  addContactMsg,
  addMsg,
  cancelMsg,
  chooseThisContactMsg,
  contactAlreadyExistsMsg,
  duplicateContactHeaderMsg,
  firstNameMsg,
  lastNameMsg,
  t,
} from '../../common/i18n';
import { Email, Name, PhoneNumber } from '../../common/react-hook-form/fields';
import { isDuplicateContactError } from '../../common/utils/contactUtils';
import { formatPhoneNumber } from '../../common/utils/phoneNumberUtils';
import { ContactRole } from '../../generated/api/contactRole';
import { ContactType as ApiContactType } from '../../generated/api/contactType';
import { DuplicateContact } from '../../generated/api/duplicateContact';
import { DuplicateContactResult } from '../../generated/api/duplicateContactResult';
import { MODAL_BUTTONS_CLASS, Modal } from '../Modal/Modal';
import type { Contact } from '../../generated/api/contact';
import type { ContactPerson } from '../../generated/api/contactPerson';
import type { GenericErrorDuplicateContact } from '../../generated/api/genericErrorDuplicateContact';

interface DuplicateContactInfo {
  type: DuplicateContact.DuplicateTypeEnum;
  duplicateContact: GenericErrorDuplicateContact;
}

interface CreateContactFormValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
}

export interface CreateContactModalProps {
  title?: string;
  onClose: () => void;
  onContactCreated: (contact: Contact) => void;
  mdmId?: string;
  roles?: ContactRole[];
}

export const CreateContactModal = ({
  title,
  onClose,
  onContactCreated,
  mdmId,
  roles = [ContactRole.ENDUSER_CONTACT],
}: CreateContactModalProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateContactInfo>();
  const [error, setError] = useState<string>();
  const [hierarchyRetryAttempted, setHierarchyRetryAttempted] = useState(false);

  const methods = useForm<CreateContactFormValues>({
    mode: 'all',
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
    },
  });

  const resetModal = () => {
    methods.reset();
    setDuplicateInfo(undefined);
    setError(undefined);
    setHierarchyRetryAttempted(false);
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
      resetModal();
    }
  };

  const createContact = async (formValues: CreateContactFormValues, forceUpsert = false) => {
    setError(undefined);
    setIsSaving(true);
    const contact: Contact = {
      contactType: ApiContactType.PERSON,
      person: {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        phoneNumber: formValues.phoneNumber,
        email: formValues.email,
        roles,
      } as ContactPerson,
    };

    try {
      const response = await upsertContactUser(contact, forceUpsert, undefined, true, mdmId);
      const responseData = await response.json();

      if (isDuplicateContactError(responseData)) {
        const duplicateType = responseData.type as DuplicateContact.DuplicateTypeEnum;

        // If duplicate is from hierarchy, automatically retry with forceUpsert=true (only once)
        if (duplicateType === DuplicateContact.DuplicateTypeEnum.DUPLICATE_FROM_HIERARCHY) {
          if (hierarchyRetryAttempted) {
            setError(t.RG74('Failed to create the new contact.'));
            return;
          }
          setHierarchyRetryAttempted(true);
          await createContact(formValues, true);
          return;
        }

        if (
          duplicateType === DuplicateContactResult.TypeEnum.CONTACT_SUPPORT ||
          duplicateType === DuplicateContactResult.TypeEnum.CONFLICTING_CONTACT
        ) {
          setError(t.RG74('Failed to create the new contact.'));
          return;
        }

        setDuplicateInfo({
          type: duplicateType,
          duplicateContact: responseData.duplicateContact,
        });
        return;
      }

      if (!response.ok) {
        setError(t.RG74('Failed to create the new contact.'));
        return;
      }
      const contactId = responseData.contactId;
      const createdContact: Contact = {
        ...contact,
        contactId,
      };
      resetModal();
      onContactCreated(createdContact);
    } catch (err) {
      setError(t.RG74('Failed to create the new contact.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (values: CreateContactFormValues) => {
    createContact(values);
  };

  const handleSelectDuplicate = () => {
    if (duplicateInfo?.duplicateContact) {
      const fullContact: Contact = {
        contactId: duplicateInfo.duplicateContact.contactId,
        contactType: ApiContactType.PERSON,
        person: {
          firstName: duplicateInfo.duplicateContact.firstName,
          lastName: duplicateInfo.duplicateContact.lastName,
          phoneNumber: duplicateInfo.duplicateContact.phoneNumber,
          email: duplicateInfo.duplicateContact.email,
        } as ContactPerson,
      };
      resetModal();
      onContactCreated(fullContact);
    }
  };

  const handleForceCreate = () => {
    const values = methods.getValues();
    setHierarchyRetryAttempted(false);
    createContact(values, true);
  };

  const handleCancel = () => {
    setDuplicateInfo(undefined);
  };

  const isPossibleDuplicate = duplicateInfo?.type === DuplicateContact.DuplicateTypeEnum.POSSIBLE_CONTACT_DUPLICATE;

  return duplicateInfo ? (
    <Modal
      closeable
      heading={isPossibleDuplicate ? t.CVJF(duplicateContactHeaderMsg) : t.USHS(contactAlreadyExistsMsg)}
      onClose={handleClose}
      size="m"
    >
      <div className="of-create-contact-modal__duplicate">
        <p>
          {isPossibleDuplicate
            ? t.AR17('Is this the user you are looking for? Select the found user or create a new user.')
            : t.HFUO('The contact you tried to create can be found in the company listing.')}
        </p>

        <div className="of-create-contact-modal__duplicate-info">
          <div>
            <strong>{t.VA44('Found contact')}:</strong>
          </div>
          <div>
            {duplicateInfo.duplicateContact.firstName} {duplicateInfo.duplicateContact.lastName}
          </div>
          <div>{duplicateInfo.duplicateContact.email}</div>
          <div>{formatPhoneNumber(duplicateInfo.duplicateContact.phoneNumber)}</div>
        </div>

        <div className={MODAL_BUTTONS_CLASS}>
          <CL.Button color="link" onClick={handleCancel}>
            {t.B2V1(cancelMsg)}
          </CL.Button>
          {isPossibleDuplicate && (
            <CL.Button color="light" onClick={handleForceCreate}>
              {t.A2WC('create a new user')}
            </CL.Button>
          )}
          <CL.Button onClick={handleSelectDuplicate}>{t.T51P(chooseThisContactMsg)}</CL.Button>
        </div>
      </div>
    </Modal>
  ) : (
    <Modal closeable heading={title || t.CSTF(addContactMsg)} onClose={handleClose} size="m">
      <FormProvider {...methods}>
        {error && (
          <CL.Disclaimer className={dsClass.MARGIN_BOTTOM_4} icon={<CL.Icon icon="warning" />} text={error} visible />
        )}

        <Name name="firstName" label={t.AIK7(firstNameMsg)} placeholder={t.AIK7(firstNameMsg)} required />
        <Name name="lastName" label={t.Y41S(lastNameMsg)} placeholder={t.Y41S(lastNameMsg)} required />
        <PhoneNumber name="phoneNumber" required />
        <Email name="email" required />

        <div className={MODAL_BUTTONS_CLASS}>
          <CL.Button color="link" onClick={handleClose} type="button" disabled={isSaving}>
            {t.B2V1(cancelMsg)}
          </CL.Button>
          <CL.Button onClick={methods.handleSubmit(handleSubmit)} loading={isSaving}>
            {t.VKFM(addMsg)}
          </CL.Button>
        </div>
      </FormProvider>
    </Modal>
  );
};
